import {
  returnAllTradeDetailsFromGenericParams,
  returnOPLegFormThisTrade,
  returnRPLegFromThisTrade,
} from "@/lib/agGridColDefs/colDefGlobalHelpers";
import { determineCurrencySymbol } from "@/lib/currencySymbolHelper";
import { ExecutionRequestStatus } from "@/lib/interfaces and Enums/ENUMExecutionRequestStatus";
import { ExecutionRequestQuoteLeg } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import {
  ExecutionStrategy,
  ISwapTradeFees,
} from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { getFlashStyles, FlashKeyframes } from "@/lib/styles/flashStyles";
import tradeDataStore from "@/store/tradeDataStore";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import { getBlockIdForFlash } from "../quoteAndExecuteFunctions/quoteFlashHelper";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import {
  statusAtAffirmedRange,
  statusAtExecutedRange,
  statusFromAwarded,
} from "../colDefs/colDefsShared";
import { showNeedToRecouponOrEnterCash } from "./DealerCellRenderer";
import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import { cubeCustomColors } from "../../../../../../tailwind.config";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { findCPOnTrade } from "../tradeLookupHelpers";

export function returnFeeTypeObjectRPLeg(
  status: ExecutionRequestStatus,
  thisFeeObject: ISwapTradeFees,
): keyof ISwapTradeFees {
  if (status) {
    if (
      !statusAtAffirmedRange.includes(status as ExecutionRequestStatus) &&
      !statusAtExecutedRange.includes(status as ExecutionRequestStatus)
    ) {
      // 14310: Awarded and below: Dealer read from ---- indicativeFeeDirection
      // Requirement does NOT mention transactionFee - However, this is storing the user
      // input value, so always read it if it's there.
      if (thisFeeObject.transactionFee != null) {
        return "transactionFee";
      }
      return "indicativeFee";
    } else {
      if (statusAtExecutedRange.includes(status as ExecutionRequestStatus)) {
        // 14310: Exectued Dealer read from  ---- transactionFeeDirection

        return "transactionFee";
      } else if (
        statusAtAffirmedRange.includes(status as ExecutionRequestStatus)
      ) {
        // 14310: Affirmed Dealer read from ---- dealtFeeDirection
        return "dealtFee";
      }
    }
  }
  return "indicativeFeeDirection";
}

export function returnCashValueRPatER({
  thisTrade,
}: {
  thisTrade: ExecutionStrategy;
}): number | string {
  // all should be on 'thisTrade' as it's the PR side.
  if (!thisTrade.fees || thisTrade.fees.length < 0) {
    return "";
  }
  const thisFeeObject = thisTrade.fees.find(
    (object) => object.transactionFeeSide == thisTrade.quoteSideER,
  );
  if (!thisFeeObject) {
    return "";
  }

  const cash = thisTrade.status == ExecutionRequestStatus.Affirmed && thisFeeObject.dealtFee != null ? thisFeeObject.dealtFee 
    : thisFeeObject.transactionFee != null ? thisFeeObject.transactionFee 
    : "";
  if (cash == undefined) {
    return "";
  }

  return cash;
}

export function returnCashValueRPatLeg({
  params, //Params will always be stale.
  thisTrade,
  isOutright,
}: {
  params: any; //Params will always be stale.
  thisTrade: ExecutionStrategy;
  isOutright: boolean;
}): number | string {
  const unwind = thisTrade.unwind;
  const cash: number | string = "";
  // Need to use the params.data to determine where within 'thisTrade' we are at.
  const thisBlockId = !isOutright
    ? params.data.blockId
    : params.data.legs?.[0]?.blockId;
  const thisBlock = thisTrade.legs?.find((leg) => leg.blockId == thisBlockId);
  if (!thisBlock) {
    return "";
  }

  if (!thisBlock.fees || thisBlock.fees.length < 0) {
    return "";
  }

  const thisFeeObject = thisBlock.fees.find(
    (object) => object.transactionFeeSide == thisBlock.quoteSide,
  );

  if (!thisFeeObject) {
    return "";
  }

  if (thisTrade.status) {
    const status = thisTrade.status as ExecutionRequestStatus;

    if (unwind == true) {
      //14310 - Only mentions

      const feeToShow = returnFeeTypeObjectRPLeg(status, thisFeeObject);
      const cash = thisFeeObject[feeToShow];
      if (cash != null && (typeof cash == "number" || typeof cash == "string")) {
        // Prevent Boolean return.
        return cash;
      }

      // 14300 - Non-NPV Style Trade
    } else if (thisBlock.unwind == "Recoupon" || thisBlock.unwind == "NPV") {
      // “status” = “Affirmed” - Cash unlikely to be at affirmed.
      if (statusAtAffirmedRange.includes(status)) {
        const cash = thisFeeObject.dealtFee;
        if (cash != null) {
          return cash;
        }
      } else if (thisBlock.unwind == "NPV") {
        // 14300 - NPV
        const cash = thisFeeObject.transactionFee;
        if (cash != null) {
          return cash;
        }
      } else if (thisBlock.unwind == "Recoupon") {
        // 14300 - Recoupon
        const cash = thisFeeObject.transactionFee;
        if (cash != null) {
          return cash;
        } else {
          const cash = thisFeeObject.indicativeFee;
          if (cash != null) {
            return cash;
          }
        }
      }
    }
  }
  return cash;
}

export function returnCashValueOPatER({
  params,
  thisTrade,
}: {
  params: any; // stale Data - used to find the ERID only
  thisTrade: ExecutionStrategy;
}): number | string {
  if (thisTrade.quoteSideES == undefined) {
    return "";
  }
  const thisERId = params.data.executionRequestId;
  const thisExecutionRequest = findCPOnTrade(thisTrade, thisERId);
  if (
    !thisExecutionRequest ||
    !thisExecutionRequest.fees ||
    thisExecutionRequest.fees.length < 0
  ) {
    return "";
  }

  const thisFeeObject = thisExecutionRequest.fees.find(
    (object: any) => object.transactionFeeSide == thisExecutionRequest.quoteSideER,
  );

  if (!thisFeeObject) {
    return "";
  }

  //Determine Cash value
  const cash = thisTrade.status == ExecutionRequestStatus.Affirmed && thisFeeObject.dealtFee != null ? thisFeeObject.dealtFee 
    : thisFeeObject.transactionFee != null ? thisFeeObject.transactionFee 
    : "";

  if (cash == undefined) {
    return "";
  }
  return cash;
}

export function returnCashValueOPatLeg({
  params,
  thisTrade,
  isOutright,
}: {
  params: any; // stale Data - used to find the ERID only
  thisTrade: ExecutionStrategy;
  isOutright: boolean;
}): number | string | null {
  let cash: number | string | null = "";
  const unwind = thisTrade.unwind;

  const thisBlockId = !isOutright
    ? params.data.blockId
    : params.data.legs?.[0]?.blockId;
  let thisLeg: ExecutionRequestQuoteLeg | null = null;

  thisTrade.executionRequestCounterParties?.forEach((ERCPs) => {
    const found = ERCPs.legs?.find((leg) => leg.blockId == thisBlockId);
    if (found) {
      thisLeg = found;
    }
  });

  if (thisLeg && thisLeg != null) {
    const thisLegFound = thisLeg as ExecutionRequestQuoteLeg;

    if (!thisLegFound.fees || thisLegFound.fees.length == 0) {
      return "";
    }

    const thisFeeObject = thisLegFound.fees.find(
      (object) => object.transactionFeeSide == thisLegFound.quoteSide,
    );

    if (!thisFeeObject) {
      return "";
    }

    if (
      unwind == true ||
      (unwind == false && thisLegFound.unwind == "Recoupon") ||
      (unwind == false && thisLegFound.unwind == "NPV")
    ) {
      if (!thisLegFound.quoteSide) {
        return "";
      }
      //14510 - “status” = “Received” to “Executed” (In the below statement, detirmiend by NOT being at exectued or affirmed clusters. )
      if (thisTrade.status) {
        if (
          !statusAtAffirmedRange.includes(thisTrade.status) &&
          !statusAtExecutedRange.includes(thisTrade.status)
        ) {
          // 14310: Awarded and below: Dealer read from ---- indicativeFeeDirection
          cash = thisFeeObject.transactionFee;
          return cash;
        } else {
          if (statusAtExecutedRange.includes(thisTrade.status)) {
            // 14310: Exectued Dealer read from  ---- transactionFeeDirection
            cash = thisFeeObject.transactionFee;
            return cash;
          } else if (statusAtAffirmedRange.includes(thisTrade.status)) {
            // 14310: Affirmed Dealer read from ---- dealtFeeDirection
            cash = thisFeeObject.dealtFee;
            return cash;
          }
        }
      }
      cash = thisFeeObject.indicativeFee;
      return cash;
    }
  }

  return cash;
}

export function calculateCashAtDataLevel(
  params: any, // Will always be stale.
  thisTrade: ExecutionStrategy,
  isOp: boolean,
  isLeg: boolean,
  isER: boolean,
  isOutright: boolean,
) {
  if (!isOp && isER && !isOutright) {
    const cash = returnCashValueRPatER({ thisTrade });
    return cash;
  } else if ((!isOp && isLeg) || (!isOp && isOutright)) {
    const cash = returnCashValueRPatLeg({ params, thisTrade, isOutright });
    return cash;
  } else if (isOp && isER && !isOutright) {
    const cash = returnCashValueOPatER({ params, thisTrade });
    return cash;
  } else if ((isOp && isLeg) || (isOp && isOutright)) {
    const cash = returnCashValueOPatLeg({ params, thisTrade, isOutright });
    return cash;
  }
  return "";
}

export function isCellNPVorRecoupon(
  params: any, // Will always be stale.
  thisTrade: ExecutionStrategy,
  isOp: boolean,
  isLeg: boolean,
  isER: boolean,
  isOutright: boolean,
) {
  if (!isOp && isER && !isOutright) {
    const isUnwind = thisTrade.unwind;
    return isUnwind;
  } else if ((!isOp && isLeg) || (!isOp && isOutright)) {
    const thisBlockId = !isOutright
      ? params.data.blockId
      : params.data.legs?.[0]?.blockId;
    const thisBlock = thisTrade.legs?.find((leg) => leg.blockId == thisBlockId);
    if (!thisBlock) {
      return "";
    }
    const isUnwind =
      thisBlock.unwind == "Recoupon" || thisBlock.unwind == "NPV";
    return isUnwind;
  } else if (isOp && isER && !isOutright) {
    const isUnwind = thisTrade.unwind;
    return isUnwind;
  } else if ((isOp && isLeg) || (isOp && isOutright)) {
    let thisLeg: ExecutionRequestQuoteLeg | null = null;
    if (!isOutright) {
      const thisBlockId = params.data.blockId;
      thisTrade.executionRequestCounterParties?.forEach((ERCPs) => {
        const found = ERCPs.legs?.find((leg) => leg.blockId == thisBlockId);
        if (found) {
          thisLeg = found;
        }
      });
    } else if (isOutright) {
      const thisERId = params.data.executionRequestId;
      thisTrade.executionRequestCounterParties?.forEach((ERCP) => {
        const found = ERCP.executionRequestId == thisERId;
        if (found && ERCP.legs?.length > 0) {
          thisLeg = ERCP.legs[0];
        }
      });
    }

    if (thisLeg && thisLeg != null) {
      const thisLegFound = thisLeg as ExecutionRequestQuoteLeg;
      const isUnwind =
        thisLegFound.unwind == "Recoupon" || thisLegFound.unwind == "NPV";
      return isUnwind;
    }
    return false;
  }
  return false;
}

export function isAbleToShowCashColumn({
  params,
  thisTrade,
  isOp,
  isLeg,
  isER,
  isOutright,
}: {
  params: any; // Will always be stale.
  thisTrade: ExecutionStrategy;
  isOp: boolean;
  isLeg: boolean;
  isER: boolean;
  isOutright: boolean;
}): boolean {
  if (thisTrade) {
    // RP
    if (!isOp && isER) {
      return true;
    } else if (!isOp && isLeg) {
      //RP LEG
      const thisBlockId = params.data.blockId;
      const thisBlock = thisTrade.legs?.find(
        (leg) => leg.blockId == thisBlockId,
      );
      if (!thisBlock) {
        return false;
      }
      const isReCoupon = thisBlock.unwind == "Recoupon";
      if (isReCoupon && thisTrade.status) {
        // if (!statusFormExecutedToAffirmedRange.includes(thisTrade.status)) {
        // #14876, #14877, #14878, #14879, #14880, #14881 - Moving ReCoupon showing on Dealer and Cash cells forward to Awarded.
        if (!statusFromAwarded.includes(thisTrade.status)) {
          return false;
        }
      }
    } else if (isOp && isER && !isOutright) {
      return true;
    } else if ((isOp && isLeg) || (isOp && isOutright)) {
      // 14694: Dealer default can be removed for an entity at status Dealt Away when an NPV Style trade (where ER.Unwind = "True")
      const thisBlockId = !isOutright
        ? params.data.blockId
        : params.data.legs?.[0]?.blockId; // Block ID now found on outrights.
      let thisLeg: ExecutionRequestQuoteLeg | null = null;

      thisTrade.executionRequestCounterParties?.forEach((ERCPs) => {
        const found = ERCPs.legs?.find((leg) => leg.blockId == thisBlockId);
        if (found) {
          thisLeg = found;
        }
      });

      if (thisLeg && thisLeg != null) {
        const thisLegFound = thisLeg as ExecutionRequestQuoteLeg;
        if (thisLegFound.statusER == ExecutionRequestStatus.DealtAway) {
          if (thisLegFound.fees && !thisLegFound.fees[0]?.transactionFee) {
            return false;
          }
        }
        // } else if (thisLegFound.statusER == ExecutionRequestStatus.DealtAway) {
        //   if (thisLegFound.fees && !thisLegFound.fees[0].indicativeFee)
        //     return false;
        //   }
        // }

        const isReCoupon = thisLegFound.unwind == "Recoupon";
        if (isReCoupon && thisTrade.status) {
          if (!statusFromAwarded.includes(thisTrade.status)) {
            // #14876, #14877, #14878, #14879, #14880, #14881 - Moving ReCoupon showing on Dealer and Cash cells forward to Awarded.
            // if (!statusFormExecutedToAffirmedRange.includes(thisTrade.status)) {
            // added from - Captures affirmed too now.
            return false;
          }
          // Hide the value in the RP Block Status in not Awarded or later - Above is looking at the status of the whole trade.
          if (
            thisLegFound &&
            thisLegFound.statusER &&
            !statusFromAwarded.includes(thisLegFound.statusER)
          ) {
            return false;
          }
        }
      }
    }
  }
  return true;
}

export function checkRPLegHasTransactionFee({
  params,
  thisTrade,
  isOp,
  isLeg,
}: {
  params: any;
  thisTrade: ExecutionStrategy;
  isOp: boolean;
  isLeg: boolean;
}) {
  if (isOp || !isLeg) {
    return false;
  } else {
    // Will ONLY be RP LEG
    const blockID = params.data.blockId;
    const currentLeg = thisTrade.legs?.find((leg) => leg.blockId == blockID);

    if (currentLeg && currentLeg.fees && currentLeg.fees.length > 0) {
      const currentFeeObject = currentLeg.fees.find(
        (feeObj: ISwapTradeFees) =>
          feeObj.transactionFeeSide == currentLeg.quoteSide,
      );
      if (currentFeeObject) {
        const currentTransactionFee = currentFeeObject.transactionFee;

        if (currentTransactionFee == undefined) {
          return false;
        } else {
          return true;
        }
      }
    }
  }

  return false;
}

const CashCellRenderer = (params: any) => {
  // const data = params.data;
  //   const tableLevel = params.colDef.cellRendererParams.tableLevel;
  const { theme } = useTheme();
  // const unwind = fullTradeData.unwind;
  const { isOp, isLeg, isER, isOutright, fullTradeData } =
    returnAllTradeDetailsFromGenericParams(params);

  const currentTradeData = tradeDataStore((state) => state.tradeData);
  const thisTrade =
    currentTradeData.find((trade) => trade.esid == fullTradeData.esid) ??
    fullTradeData;

  const helperObject = { params, thisTrade, isOp, isER, isOutright, isLeg };
  const thisLeg = helperObject
    ? isOp
      ? returnOPLegFormThisTrade(helperObject)
      : returnRPLegFromThisTrade(helperObject)
    : undefined;

  const currencySymbol = thisTrade
    ? determineCurrencySymbol({ thisTrade, thisLeg, isLeg, isER, isOutright })
    : "$";

  const [value, setValue] = useState(
    thisTrade
      ? calculateCashAtDataLevel(
          params,
          thisTrade,
          isOp,
          isLeg,
          isER,
          isOutright,
        )
      : "",
  );

  const [canValueBeShown, setCanValueBeShown] = useState(
    thisTrade
      ? isAbleToShowCashColumn({
          params,
          thisTrade,
          isOp,
          isLeg,
          isER,
          isOutright,
        })
      : false,
  );

  const [showRequiredStyleCSS, setShowRequiredStyleCSS] = useState(
    thisTrade
      ? isCellNPVorRecoupon(params, thisTrade, isOp, isLeg, isER, isOutright)
      : false,
  );

  //Value to see if a recoupon has been sent to the counterparty yet when quoting as the RP.
  const [warningNoRecouponOrCashEntered, setwarningNoRecouponOrCashEntered] =
    useState(
      thisTrade
        ? showNeedToRecouponOrEnterCash(params, thisTrade, isOutright)
        : false,
    );

  const [rpLegHasTransactionFee, setRpLegHasTransactionFee] = useState(
    thisTrade
      ? checkRPLegHasTransactionFee({ params, thisTrade, isOp, isLeg })
      : false,
  );

  // Flash validation: subscribe to legCellsToFlash
  const legCellsToFlash = tradeSettingsStore(
    (state) => state.legCellsToFlash,
  );

  const blockId = getBlockIdForFlash(isLeg, isER, isOutright, params);

  const isFlashing =
    blockId != null &&
    legCellsToFlash.some(
      (cell) =>
        cell.esid === fullTradeData.esid &&
        cell.blockId == blockId &&
        (!cell.field || cell.field === "cash"),
    );

  const flashStyles = getFlashStyles(isFlashing);

  useEffect(() => {
    if (thisTrade) {
      const cash = calculateCashAtDataLevel(
        params,
        thisTrade,
        isOp,
        isLeg,
        isER,
        isOutright,
      );
      setValue(cash);
    }
    // CHECK IS CELL IS RENDERED AND CSS UPDATES:
    if (thisTrade) {
      setCanValueBeShown(
        isAbleToShowCashColumn({
          params,
          thisTrade,
          isOp,
          isLeg,
          isER,
          isOutright,
        }),
      );
      setShowRequiredStyleCSS(
        isCellNPVorRecoupon(params, thisTrade, isOp, isLeg, isER, isOutright),
      );
      setwarningNoRecouponOrCashEntered(
        showNeedToRecouponOrEnterCash(params, thisTrade, isOutright),
      );
      setRpLegHasTransactionFee(
        checkRPLegHasTransactionFee({ params, thisTrade, isOp, isLeg }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thisTrade]);

  const cubeBase700 = cubeCustomColors.cubeBase[700];
  const cubeBaseDark700 = cubeCustomColors.cubeBaseDark[700];

  function returnTooltipMessage(thisTrade: ExecutionStrategy): string {
    const status = thisTrade.status;
    const isOp = thisTrade.role == "OP";

    if (!isOp) {
      if (
        status == ExecutionStrategyStatus.Received ||
        status == ExecutionStrategyStatus.ReSent
      ) {
        if (thisTrade && thisTrade.unwind) {
          return "Transaction fee required.";
        } else {
          return "Transaction fee required.";
        }
      }

      if (status == ExecutionStrategyStatus.Awarded) {
        if (
          params.data.unwind == "Recoupon" ||
          (isOutright && params.data.legs?.[0]?.unwind == "Recoupon")
        ) {
          // return "Unwind fee required to execute. Currently showing indicative value. Click into cash cell to edit.";
          return "Transaction fee required.";
        } else if (
          params.data.unwind == "NPV" ||
          (isOutright && params.data.legs?.[0]?.unwind == "NPV")
        ) {
          return "Transaction fee required.";
        }
      }
    } // #14876, #14877, #14878, #14879, #14880, #14881 - OP should no longer be able to get to get to exectued without the unwind fees populated.
    else if (isOp) {
      if (status == ExecutionStrategyStatus.Awarded) {
        return "Counterparty will execute with the transaction fee.";
      }
    }
    return "";
  }

  const tooltipID = crypto.randomUUID();

  return canValueBeShown ? (
    <>
    <FlashKeyframes isFlashing={isFlashing} />
    <div
      data-testid="CashCellRendererContainer"
      id="CashCellRendererContainer"
      className="flex h-full w-full items-center gap-1"
    >
      {warningNoRecouponOrCashEntered ? (
        <span
          id="NPVRecouponInfo"
          data-tooltip-id={`needToRecoupon${tooltipID}`}
          className="flex w-4 text-lg text-red-400"
        >
          🛈
        </span>
      ) : (
        <span></span>
      )}
      <div
        className={
          isFlashing
            ? ""
            : showRequiredStyleCSS
              ? `mr-1 h-full text-ellipsis border-[0px] pl-1 ${
                  theme == "light"
                    ? `inputCellLight ${warningNoRecouponOrCashEntered ? "inputCellMissingInfo" : ""}`
                    : `inputCellDark ${warningNoRecouponOrCashEntered ? "inputCellMissingInfo" : ""}`
                } ${warningNoRecouponOrCashEntered && !rpLegHasTransactionFee ? " text-gray-400 dark:text-zinc-500" : ""}`
              : ""
        }
        style={isFlashing ? flashStyles : undefined}
      >
        {value != null && value !== "" ? (
          currencySymbol +
          Math.abs(Number(value)).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        ) : (
          <span className="text-gray-300 dark:text-zinc-500">
            {showRequiredStyleCSS && currencySymbol}
          </span>
        )}
      </div>
      {createPortal(
        <Tooltip
          id={`needToRecoupon${tooltipID}`}
          place="left"
          variant="info"
          className=" z-20"
          clickable={true}
          // openOnClick={true}
          opacity={1}
          style={{
            color: theme == "dark" ? "white" : "black",
            backgroundColor: theme == "dark" ? cubeBaseDark700 : cubeBase700,
            margin: "0px",
            padding: "2px",
            paddingLeft: "5px",
            paddingRight: "5px",
          }}
          // offset={}
          border="1px solid gray"
        >
          <div id="addWidget" className="w-full pb-1">
            <div className="flex w-[300px] flex-col gap-1 ">
              {thisTrade && returnTooltipMessage(thisTrade)}
            </div>
          </div>
        </Tooltip>,
        document.body,
      )}
    </div>
    </>
  ) : (
    <div></div>
  );
};

export default CashCellRenderer;
